import classNames from "classnames";
import { useResponsive } from "ahooks";
import { useState, useEffect } from "react";
import i18n from "@breward/i18n";

interface LevelSelectProps {
  id?: string;
  name?: string;
  label?: string;
  value?: number | number[];
  mode?: "single" | "range";
  readOnly?: boolean;
  min?: number;
  max?: number;
  step?: number;
  stepsLabels?: Record<number, string>;
  onChange?: (value: number | number[]) => void;
}

export const LevelSelect = ({
  id,
  name,
  label,
  value,
  mode = "single",
  readOnly = false,
  min = 0,
  max = 5,
  step = 1,
  stepsLabels,
  onChange,
}: LevelSelectProps) => {
  const [range, setRange] = useState<number[] | undefined>();
  const responsive = useResponsive();

  // Calculate how many options to display per row based on screen size
  const getColSpan = () => {
    if (responsive?.xl) return 2; // 6 options per row on XL screens
    if (responsive?.lg) return 3; // 4 options per row on large screens
    if (responsive?.md) return 4; // 3 options per row on medium screens
    return 6; // 2 options per row on small screens
  };

  const colSpan = getColSpan();

  const handleChange = (value: number) => {
    if (mode === "range") {
      if (range?.length === 2) {
        setRange([value]);
      } else if (range?.length === 1) {
        const newRange = range?.concat(value).sort((a, b) => a - b);
        setRange(newRange);
        onChange && onChange(newRange);
      } else {
        setRange([value]);
      }
    } else {
      onChange && onChange(value);
    }
  };

  useEffect(() => {
    if (mode === "range") {
      setRange((value as number[])?.sort((a, b) => a - b) as number[]);
    }
  }, [value]);

  const valueSelected = (index: number) =>
    mode === "single"
      ? value === index
      : range?.length === 2 && index >= range[0] && index <= range[1];

  const stepsCount = Math.ceil((max - min + 1) / step);
  const levels = Array.from({ length: stepsCount }, (_, i) => i);

  // Render the component in read-only mode
  if (readOnly) {
    return (
      <div className="px-3 py-2 font-sans rounded-lg border border-solid border-primary-300 dark:border-primary-800 bg-base-400 dark:bg-content-800 shadow-sm transition-colors">
        <div className="flex items-center justify-between">
          <div className="rounded-full bg-primary-500 dark:bg-primary-600 text-white text-md font-medium px-2 py-0.5">
            <div className="text-white font-medium">
              {typeof value === 'undefined'
                ? i18n.t("global:shared.levelSelect.emptyValue", "N/A")
                : value}
            </div>
          </div>
          {stepsLabels?.[value as number] && (
            <div className="rounded-full bg-primary-500 dark:bg-primary-600 text-white text-xs font-medium px-2 py-0.5">
              <div className="text-white font-medium">
                {stepsLabels[value as number]}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans rounded-lg border border-primary-100 dark:border-primary-800 bg-base-50 dark:bg-content-800 shadow-sm transition-colors">
      <div className="px-4 pt-3 border-b border-primary-100 dark:border-primary-800">
        <div className="text-primary-900 dark:text-primary-100 font-medium text-sm">
          {label}
        </div>
      </div>

      <div className="px-4 pb-3">
        <div className="grid grid-cols-12 gap-1.5 sm:gap-2">
          {levels.map((levelValue, index) => {
            const isSelected = valueSelected(levelValue);
            return (
              <div
                data-cy={`level-select-${name}-level-${levelValue}-button`}
                key={levelValue}
                className={classNames(
                  `col-span-${colSpan}`,
                  "relative"
                )}
              >
                <button
                  type="button"
                  onClick={() => handleChange(levelValue)}
                  className={classNames(
                    "w-full py-2 px-1 sm:px-2 rounded-md transition-all duration-150 border border-primary-100 dark:border-primary-800",
                    "flex flex-col items-center justify-center text-center",
                    "hover:shadow-md hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-primary-500/50",
                    isSelected
                      ? "bg-primary-500 dark:bg-primary-600 text-white shadow-md z-10 border-primary-400 dark:border-primary-500"
                      : "bg-white dark:bg-content-700 hover:bg-primary-50 dark:hover:bg-primary-900/30"
                  )}
                >
                  <div className={classNames(
                    "font-medium",
                    isSelected
                      ? "text-white"
                      : "text-primary-700 dark:text-primary-300"
                  )}>
                    {levelValue}
                  </div>
                  {stepsLabels && stepsLabels[levelValue] && (
                    <div className={classNames(
                      "text-xs truncate w-full px-1 mt-0.5",
                      isSelected
                        ? "text-white/90"
                        : "text-primary-600/80 dark:text-primary-400/80"
                    )}>
                      {stepsLabels[levelValue]}
                    </div>
                  )}
                </button>

                {(mode === "range" && range?.length === 1 && range[0] === levelValue) && (
                  <div className="absolute -inset-px rounded-md ring-2 ring-primary-500/50 dark:ring-primary-400/50 animate-pulse pointer-events-none" />
                )}
              </div>
            );
          })}
        </div>

        {/* Range selection display */}
        {mode === "range" && range?.length === 2 && (
          <div className="mt-3 pt-3 border-t border-primary-100 dark:border-primary-800 flex items-center justify-end">
            <div className="text-primary-700 dark:text-primary-300 font-medium text-sm mr-2">
              {i18n.t("global:shared.levelSelect.rangeSelection", "Selected range:")}
            </div>
            <div className="flex items-center gap-1">
              <div className="bg-primary-500 dark:bg-primary-600 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                {range[0]}
              </div>
              <span className="text-primary-500 dark:text-primary-400">-</span>
              <div className="bg-primary-500 dark:bg-primary-600 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                {range[1]}
              </div>
              {stepsLabels && (
                <div className="text-primary-900 dark:text-primary-100 text-sm font-medium ml-1">
                  ({stepsLabels[range[0]]} - {stepsLabels[range[1]]})
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
